import { createContext, useContext, useEffect, useState } from "react";

// Central translation system. Language dictionaries live here (one block per language)
// and are consumed via the useT() hook. New keys should be added to every language.
export const LANGS = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "kn", label: "Kannada", native: "ಕನ್ನಡ" },
  { code: "te", label: "Telugu", native: "తెలుగు" },
];

const dict = {
  en: {
    // Login
    brand: "Rotary GOV Report Builder",
    login_hero_kicker: "District 3192 · RY 2026-27",
    login_hero_title: "The official operations platform for the Governor’s Visit.",
    login_hero_sub: "Secure project-owned sign-in. Your credentials and sessions never leave the platform’s own servers.",
    login_hero_secure: "HttpOnly session · Argon2id credential storage",
    signin_kicker: "Sign in",
    signin_title: "Sign in to Rotary GOV Report Builder",
    email: "Email",
    password: "Password",
    signin_btn: "Sign in",
    forgot: "Forgot your password?",
    login_footer: "Secure server-side session · no tokens in the browser",
    invalid_login: "Invalid email or password",
    language: "Language",
    // Shared
    loading: "Loading…",
    save: "Save",
    // Profile
    consent: "I authorise Rotary and my club to store and use the personal details I provide here for the club directory, official Rotary communication and the Governor’s Official Visit. I confirm the information is accurate.",
    prof_consent_title: "Authorisation & consent",
    prof_directory_details: "Directory Details",
    prof_no_member_title: "No linked member",
    prof_no_member_sub: "Your account is not linked to a member record, so there is no profile to edit.",
    prof_complete: "Profile complete",
    prof_incomplete: "Profile incomplete",
    prof_call_name: "Call name",
    prof_mobile: "Mobile",
    prof_classification: "Classification",
    prof_spouse_name: "Spouse name",
    prof_address: "Address",
    prof_occasions: "Occasions · day & month only",
    prof_birthday: "My birthday",
    prof_spouse_birthday: "Spouse's birthday",
    prof_anniversary: "Anniversary",
    prof_privacy: "Privacy: we never store the year, full date of birth or age.",
    prof_save: "Save profile",
    prof_saved: "Profile saved",
    // Club dashboard
    club_loading: "Loading your club home…",
    club_load_error: "Unable to load your club home.",
    club_govs_visit: "Governor's visit",
    club_not_scheduled: "Not scheduled yet",
    club_mock_gov: "Mock GOV",
    club_schedule_now: "Schedule now",
    club_confirm: "Confirm",
    club_proposed: "Proposed",
    club_schedule_mock_title: "Schedule the Mock GOV",
    club_schedule_mock_desc: "The AG visits ~5–10 days before the GOV to check readiness. Pick a date before the Governor's visit; the AG confirms it.",
    club_propose_date: "Propose date",
    club_pick_date: "Pick a date",
    club_add_calendar: "Add to calendar",
    club_sync: "Sync (auto-update)",
    club_action_points_title: "Recommended action points by the district",
    club_board_title: "Board of Directors",
    club_board_ready: "Board is ready",
    club_board_assign: "Assign board positions",
    club_committees_title: "Committee appointments",
    club_committees_done: "Committees appointed",
    club_committees_assign: "Appoint chairs & members",
    club_directory_title: "Build club directory",
    club_directory_ready: "Generate the roster & directory",
    club_directory_locked: "Finish the board & committees first",
    club_manage_members: "Manage club members",
    club_registered: "Members on My Rotary",
    club_gender_mix: "Gender mix",
    club_gender_mix_sub: "Help the district see your club's diversity.",
    club_total_membership: "Total membership",
    club_board: "Board",
    club_new_inducted: "New members inducted this year",
    club_male: "Male",
    club_female: "Female",
    club_other: "Other",
    club_documents: "Documents",
    club_docs_admin: "Admin",
    club_docs_financial: "Financial",
    club_no_doc_list: "The district hasn't published this year's document list yet.",
    club_submit: "Submit to district",
    club_submitted: "Submitted to district",
    club_submitted_incomplete: "Submitted to district (some items outstanding)",
    club_foundation_giving: "Foundation giving",
    club_foundation_sub: "The Rotary Foundation contributions (USD).",
    club_total_prev: "Total till previous year",
    club_this_year: "This year",
    club_annual_fund: "Annual Fund",
    club_polio_plus: "PolioPlus Fund",
    club_other_funds: "Other Funds",
    club_endowment: "Endowment Fund",
    club_total_this_year: "Total this year",
    club_per_capita: "Per capita this year",
    club_recognitions: "Recognitions",
    club_global_grants: "Global Grants",
    club_grants_sub: "Projects and funding shares (USD)",
    club_ongoing: "Carried forward / Ongoing Grant Projects",
    club_initiated_this_year: "Current year initiated Grant Projects",
    club_total_grant_funding: "Total grant funding",
    club_all_board_filled: "All board positions are filled",
    club_no_visit: "No visit planned yet.",
    // District dashboard
    dist_loading: "Loading your district…",
    dist_loading_hint: "Gathering every club's readiness — this can take a few seconds.",
    dist_load_failed: "Couldn't load the district dashboard.",
    dist_load_failed_hint: "The connection may have timed out. Please try again.",
    retry: "Try again",
    dist_good_day: "Good day",
    dist_overview_sub: "Your Governor's Official Visit overview for the year.",
    dist_clubs: "Clubs",
    dist_completed: "Completed",
    dist_in_progress: "In progress",
    dist_not_started: "Not started",
    dist_find_club: "Find a club…",
    dist_all_clubs: "All clubs",
    dist_all_zones: "All zones",
    dist_clubs_count: "clubs",
    dist_zone_governor: "Zone Governor",
    dist_ag: "AG",
    dist_unassigned_ag: "Unassigned AG",
    gp_by_ag: "Performance per Assistant Governor",
    gp_view_all: "View all",
    gp_ags_label: "Assistant Governors",
    gp_ag_group: "AG group",
    dist_card_view: "Card view",
    dist_table_view: "Table view",
    dist_no_zones: "No zones or clubs are set up for this Rotary Year yet.",
    dist_no_clubs: "No clubs found.",
    dist_upcoming_visits: "Upcoming visits",
    dist_no_visits: "No visits scheduled yet.",
    dist_date_tbc: "Date TBC",
    dist_my_rotary_reg: "My Rotary registration",
    dist_across_district: "Across the whole district",
    dist_registered: "registered",
    dist_not_registered: "not registered",
    dist_clubs_done: "clubs done",
    dist_col_club: "Club",
    dist_col_zone: "Zone",
    dist_col_gov_date: "GOV date",
    dist_col_mock: "Mock GOV",
    dist_col_docs: "Docs %",
    dist_col_submission: "Submission",
    dist_mock_done: "Done",
    dist_mock_proposed: "Proposed",
    dist_mock_gov_done: "Mock GOV done",
    dist_mock_gov_proposed: "Mock GOV proposed",
    dist_submitted: "Submitted",
    dist_submitted_incomplete: "Submitted · incomplete",
    dist_not_submitted: "Not submitted",
    // Giving panel
    gp_section_title: "Foundation, Grants & Membership",
    gp_export: "Export Excel",
    gp_membership: "Membership",
    gp_whole_district: "Whole district",
    gp_no_inductions: "No inductions recorded yet.",
    gp_total_label: "Total",
    gp_per_capita: "Per capita",
    gp_fund_split: "Giving by category",
    gp_to_date: "to date",
    gp_select_zone_hint: "Select a zone above to see each club's giving by fund.",
    gp_projects_underway: "Projects underway",
    gp_underway_sub: "Ongoing + initiated this year",
    gp_total_outlay: "Total outlay",
    gp_outlay_sub: "Funding across ongoing projects",
    gp_club_giving_by_fund: "club giving by fund",
    gp_polio: "PolioPlus",
    gp_endowment_short: "Endowment",
    gp_scope_year_suffix: "this year",
    gp_this_yr: "this yr",
    gp_till_last_yr: "till last yr",
    gp_rec_sub: "Part of Foundation giving · counts",
    gp_no_phf: "No PHF / Major Donor data yet.",
    gp_by_club: "By club",
    gp_till_last_year: "Till last year",
    gp_no_clubs_zone: "No clubs in this zone yet.",
    gp_no_data: "No data entered yet.",
    // Club review
    rv_loading_roster: "Loading roster…",
    rv_not_built: "Not built yet.",
    rv_vacant: "Vacant",
    rv_position_ph: "Position",
    rv_vacant_opt: "— Vacant —",
    rv_add_position: "Add position",
    rv_save_roster: "Save roster",
    rv_kind_info: "Fill in the app",
    rv_kind_template: "Templated Excel",
    rv_kind_both: "Build in app or upload",
    rv_kind_doc: "Document upload",
    rv_optional: "optional",
    rv_not_filled: "Not filled in yet.",
    rv_edit: "Edit",
    rv_fill_in: "Fill in",
    rv_cancel: "Cancel",
    rv_no_file: "No file uploaded yet.",
    rv_upload_more: "Upload another (image, PDF, Word or Excel)",
    rv_upload_first: "Upload file (image, PDF, Word or Excel)",
    rv_needs_improvement: "Needs improvement",
    rv_awaiting_rereview: "Corrected — awaiting re-review",
    rv_marked_ok: "Marked OK",
    rv_approved: "Approved",
    rv_not_approved: "Not approved",
    rv_view: "View",
    rv_new_inductees: "New inductees",
    rv_discussion_points: "Discussion points",
    rv_discussion_sub: "Points raised for this club, with who contributed them",
    rv_add_point: "Add a discussion point",
    rv_add: "Add",
    rv_no_points: "No discussion points yet.",
    rv_open: "Open",
    rv_resolved: "Resolved",
    rv_reopen: "Reopen",
    rv_resolve: "Resolve",
    rv_note: "Note",
    rv_final_action_points: "Final action points",
    rv_action_sub: "Agreed actions from the visit, each assigned to a person",
    rv_shared_with_club: "Shared with club",
    rv_gov_closed: "GOV closed",
    rv_action_point_ph: "Action point",
    rv_assigned_to_ph: "Assigned to",
    rv_instructions_ph: "Instructions (optional)",
    rv_assigned_to: "Assigned to",
    rv_unassigned: "Unassigned",
    rv_no_action_points: "No action points recorded.",
    rv_add_action_point: "Add action point",
    rv_save_action_points: "Save action points",
    rv_close_gov: "Close GOV",
    rv_close_warning: "Once you close the GOV, the whole report is locked and no further edits can be made. You can still recall shared action points later to reopen them for editing.",
    rv_share_prompt: "Share these action points with the club?",
    rv_share_prompt_sub: "Some notes may be district-only. Default is to keep them private.",
    rv_close_private: "Close · keep private",
    rv_close_share: "Close & share with club",
    rv_shared_status: "Shared with the club",
    rv_private_status: "Private to the district — recall reopens editing",
    rv_recall: "Recall from club",
    rv_share: "Share with club",
    rv_directory_pdf: "Club Directory (PDF)",
    rv_no_requirements: "No requirements were defined for this year.",
    rv_gov_visit_inv: "GOV visit & invitations",
    rv_no_access: "You don't have access to this club, or it hasn't started preparation.",
    rv_club_report: "Club GOV Report",
    rv_closed_ro: "This GOV is closed — read only",
    rv_submitted_incomplete: "Submitted (some items outstanding)",
    rv_submitted_by_club: "Submitted by the club",
    rv_prep_in_progress: "Preparation in progress",
    rv_breadcrumb_district: "District",
    rv_not_set: "Not set",
    rv_confirmed: "Confirmed",
    rv_members: "Members",
    rv_registered_cap: "Registered",
    rv_board_gender: "Board gender",
    rv_prompt_improve: "What needs improvement? (comment for the club)",
    rv_prompt_note: "Add a note for this point",
    rv_or_upload: "…or upload a document instead",
    rv_notify_officials: "Notify Club Officials",
    rv_notify_hint: "Emails the President & Secretary (cc the Assistant Governor) with the current visit status.",
    rv_notify_placeholder: "Add a comment for the club (optional)",
    rv_notify_send: "Send notification",
    rv_notify_sending: "Sending…",
    rv_notify_sent: "Club officials notified",
    rv_notify_failed: "Could not send the notification",
    rv_notify_no_officials: "No President or Secretary on record for this club",
    rv_notify_last: "Last notified",
    rv_invite_officers: "Invite officers to log in",
    rv_invite_sending: "Sending invites…",
    rv_invite_sent: "{n} officer invitation(s) emailed",
    rv_invite_active: "Officers already have active logins",
    rv_invite_none: "No new invitations were needed",
    rv_invite_failed: "Could not send officer invitations",
  },
  hi: {
    brand: "रोटरी GOV रिपोर्ट बिल्डर",
    login_hero_kicker: "डिस्ट्रिक्ट 3192 · रोटरी वर्ष 2026-27",
    login_hero_title: "गवर्नर की आधिकारिक यात्रा के लिए आधिकारिक संचालन मंच।",
    login_hero_sub: "सुरक्षित, प्लेटफ़ॉर्म-स्वामित्व वाला साइन-इन। आपके क्रेडेंशियल और सत्र कभी भी प्लेटफ़ॉर्म के अपने सर्वर से बाहर नहीं जाते।",
    login_hero_secure: "HttpOnly सत्र · Argon2id क्रेडेंशियल भंडारण",
    signin_kicker: "साइन इन करें",
    signin_title: "रोटरी GOV रिपोर्ट बिल्डर में साइन इन करें",
    email: "ईमेल",
    password: "पासवर्ड",
    signin_btn: "साइन इन करें",
    forgot: "अपना पासवर्ड भूल गए?",
    login_footer: "सुरक्षित सर्वर-साइड सत्र · ब्राउज़र में कोई टोकन नहीं",
    invalid_login: "अमान्य ईमेल या पासवर्ड",
    language: "भाषा",
    loading: "लोड हो रहा है…",
    save: "सहेजें",
    consent: "मैं रोटरी और अपने क्लब को अधिकृत करता/करती हूँ कि वे यहाँ दी गई मेरी व्यक्तिगत जानकारी को क्लब निर्देशिका, आधिकारिक रोटरी संचार और गवर्नर की आधिकारिक यात्रा के लिए संग्रहीत और उपयोग करें। मैं पुष्टि करता/करती हूँ कि जानकारी सही है।",
    prof_consent_title: "प्राधिकरण और सहमति",
    prof_directory_details: "निर्देशिका विवरण",
    prof_no_member_title: "कोई सदस्य लिंक नहीं",
    prof_no_member_sub: "आपका खाता किसी सदस्य रिकॉर्ड से लिंक नहीं है, इसलिए संपादित करने के लिए कोई प्रोफ़ाइल नहीं है।",
    prof_complete: "प्रोफ़ाइल पूर्ण",
    prof_incomplete: "प्रोफ़ाइल अपूर्ण",
    prof_call_name: "पुकारने का नाम",
    prof_mobile: "मोबाइल",
    prof_classification: "वर्गीकरण",
    prof_spouse_name: "जीवनसाथी का नाम",
    prof_address: "पता",
    prof_occasions: "अवसर · केवल दिन और महीना",
    prof_birthday: "मेरा जन्मदिन",
    prof_spouse_birthday: "जीवनसाथी का जन्मदिन",
    prof_anniversary: "वर्षगाँठ",
    prof_privacy: "गोपनीयता: हम कभी भी वर्ष, पूरी जन्मतिथि या उम्र संग्रहीत नहीं करते।",
    prof_save: "प्रोफ़ाइल सहेजें",
    prof_saved: "प्रोफ़ाइल सहेजी गई",
    club_loading: "आपका क्लब होम लोड हो रहा है…",
    club_load_error: "आपका क्लब होम लोड नहीं हो सका।",
    club_govs_visit: "गवर्नर की यात्रा",
    club_not_scheduled: "अभी निर्धारित नहीं",
    club_mock_gov: "मॉक GOV",
    club_schedule_now: "अभी निर्धारित करें",
    club_confirm: "पुष्टि करें",
    club_proposed: "प्रस्तावित",
    club_schedule_mock_title: "मॉक GOV निर्धारित करें",
    club_schedule_mock_desc: "AG तैयारी जाँचने के लिए GOV से लगभग 5–10 दिन पहले आते हैं। गवर्नर की यात्रा से पहले की तारीख चुनें; AG इसकी पुष्टि करेंगे।",
    club_propose_date: "तारीख प्रस्तावित करें",
    club_pick_date: "तारीख चुनें",
    club_add_calendar: "कैलेंडर में जोड़ें",
    club_sync: "सिंक (स्वतः-अपडेट)",
    club_action_points_title: "डिस्ट्रिक्ट द्वारा अनुशंसित कार्य बिंदु",
    club_board_title: "निदेशक मंडल",
    club_board_ready: "बोर्ड तैयार है",
    club_board_assign: "बोर्ड पद नियुक्त करें",
    club_committees_title: "समिति नियुक्तियाँ",
    club_committees_done: "समितियाँ नियुक्त",
    club_committees_assign: "अध्यक्ष और सदस्य नियुक्त करें",
    club_directory_title: "क्लब निर्देशिका बनाएँ",
    club_directory_ready: "रोस्टर और निर्देशिका बनाएँ",
    club_directory_locked: "पहले बोर्ड और समितियाँ पूरी करें",
    club_manage_members: "क्लब सदस्य प्रबंधित करें",
    club_registered: "My Rotary पर सदस्य",
    club_gender_mix: "लिंग अनुपात",
    club_gender_mix_sub: "डिस्ट्रिक्ट को अपने क्लब की विविधता देखने में मदद करें।",
    club_total_membership: "कुल सदस्यता",
    club_board: "बोर्ड",
    club_new_inducted: "इस वर्ष शामिल नए सदस्य",
    club_male: "पुरुष",
    club_female: "महिला",
    club_other: "अन्य",
    club_documents: "दस्तावेज़",
    club_docs_admin: "प्रशासन",
    club_docs_financial: "वित्तीय",
    club_no_doc_list: "डिस्ट्रिक्ट ने इस वर्ष की दस्तावेज़ सूची अभी प्रकाशित नहीं की है।",
    club_submit: "डिस्ट्रिक्ट को सबमिट करें",
    club_submitted: "डिस्ट्रिक्ट को सबमिट किया गया",
    club_submitted_incomplete: "डिस्ट्रिक्ट को सबमिट किया गया (कुछ आइटम शेष)",
    club_foundation_giving: "फ़ाउंडेशन दान",
    club_foundation_sub: "द रोटरी फ़ाउंडेशन योगदान (USD)।",
    club_total_prev: "पिछले वर्ष तक कुल",
    club_this_year: "इस वर्ष",
    club_annual_fund: "वार्षिक निधि",
    club_polio_plus: "पोलियोप्लस निधि",
    club_other_funds: "अन्य निधियाँ",
    club_endowment: "एंडोमेंट निधि",
    club_total_this_year: "इस वर्ष का कुल",
    club_per_capita: "इस वर्ष प्रति व्यक्ति",
    club_recognitions: "मान्यताएँ",
    club_global_grants: "ग्लोबल ग्रांट्स",
    club_grants_sub: "परियोजनाएँ और वित्त पोषण हिस्सेदारी (USD)",
    club_ongoing: "जारी",
    club_initiated_this_year: "इस वर्ष शुरू",
    club_total_grant_funding: "कुल अनुदान वित्त पोषण",
    club_all_board_filled: "सभी बोर्ड पद भरे गए हैं",
    club_no_visit: "अभी कोई यात्रा नियोजित नहीं।",
    dist_loading: "आपका डिस्ट्रिक्ट लोड हो रहा है…",
    dist_loading_hint: "हर क्लब की तैयारी जुटाई जा रही है — इसमें कुछ सेकंड लग सकते हैं।",
    dist_load_failed: "डिस्ट्रिक्ट डैशबोर्ड लोड नहीं हो सका।",
    dist_load_failed_hint: "कनेक्शन का समय समाप्त हो सकता है। कृपया पुनः प्रयास करें।",
    retry: "पुनः प्रयास करें",
    dist_good_day: "नमस्ते",
    dist_overview_sub: "इस वर्ष के लिए आपकी गवर्नर की आधिकारिक यात्रा का अवलोकन।",
    dist_clubs: "क्लब",
    dist_completed: "पूर्ण",
    dist_in_progress: "प्रगति पर",
    dist_not_started: "शुरू नहीं हुआ",
    dist_find_club: "क्लब खोजें…",
    dist_all_clubs: "सभी क्लब",
    dist_all_zones: "सभी ज़ोन",
    dist_clubs_count: "क्लब",
    dist_zone_governor: "ज़ोन गवर्नर",
    dist_ag: "AG",
    dist_card_view: "कार्ड दृश्य",
    dist_table_view: "तालिका दृश्य",
    dist_no_zones: "इस रोटरी वर्ष के लिए अभी कोई ज़ोन या क्लब सेट अप नहीं है।",
    dist_no_clubs: "कोई क्लब नहीं मिला।",
    dist_upcoming_visits: "आगामी यात्राएँ",
    dist_no_visits: "अभी कोई यात्रा निर्धारित नहीं।",
    dist_date_tbc: "तारीख तय होनी है",
    dist_my_rotary_reg: "My Rotary पंजीकरण",
    dist_across_district: "पूरे डिस्ट्रिक्ट में",
    dist_registered: "पंजीकृत",
    dist_not_registered: "पंजीकृत नहीं",
    dist_clubs_done: "क्लब पूर्ण",
    dist_col_club: "क्लब",
    dist_col_zone: "ज़ोन",
    dist_col_gov_date: "GOV तारीख",
    dist_col_mock: "मॉक GOV",
    dist_col_docs: "दस्तावेज़ %",
    dist_col_submission: "सबमिशन",
    dist_mock_done: "पूर्ण",
    dist_mock_proposed: "प्रस्तावित",
    dist_mock_gov_done: "मॉक GOV पूर्ण",
    dist_mock_gov_proposed: "मॉक GOV प्रस्तावित",
    dist_submitted: "सबमिट किया गया",
    dist_submitted_incomplete: "सबमिट · अपूर्ण",
    dist_not_submitted: "सबमिट नहीं किया गया",
    gp_section_title: "फ़ाउंडेशन, ग्रांट्स और सदस्यता",
    gp_export: "एक्सेल निर्यात करें",
    gp_membership: "सदस्यता",
    gp_whole_district: "पूरा डिस्ट्रिक्ट",
    gp_no_inductions: "अभी तक कोई प्रवेश दर्ज नहीं।",
    gp_total_label: "कुल",
    gp_per_capita: "प्रति व्यक्ति",
    gp_fund_split: "इस वर्ष का निधि विभाजन",
    gp_select_zone_hint: "प्रत्येक क्लब का निधि अनुसार दान देखने के लिए ऊपर एक ज़ोन चुनें।",
    gp_projects_underway: "चल रही परियोजनाएँ",
    gp_underway_sub: "जारी + इस वर्ष शुरू",
    gp_total_outlay: "कुल व्यय",
    gp_outlay_sub: "जारी परियोजनाओं में वित्त पोषण",
    gp_club_giving_by_fund: "निधि अनुसार क्लब दान",
    gp_polio: "पोलियोप्लस",
    gp_endowment_short: "एंडोमेंट",
    gp_scope_year_suffix: "इस वर्ष",
    gp_this_yr: "इस वर्ष",
    gp_till_last_yr: "पिछले वर्ष तक",
    gp_rec_sub: "फ़ाउंडेशन दान का हिस्सा · गिनती",
    gp_no_phf: "अभी तक कोई PHF / मेजर डोनर डेटा नहीं।",
    gp_by_club: "क्लब अनुसार",
    gp_till_last_year: "पिछले वर्ष तक",
    gp_no_clubs_zone: "इस ज़ोन में अभी कोई क्लब नहीं।",
    gp_no_data: "अभी तक कोई डेटा दर्ज नहीं।",
    rv_loading_roster: "रोस्टर लोड हो रहा है…",
    rv_not_built: "अभी तक नहीं बनाया गया।",
    rv_vacant: "रिक्त",
    rv_position_ph: "पद",
    rv_vacant_opt: "— रिक्त —",
    rv_add_position: "पद जोड़ें",
    rv_save_roster: "रोस्टर सहेजें",
    rv_kind_info: "ऐप में भरें",
    rv_kind_template: "टेम्पलेटेड एक्सेल",
    rv_kind_both: "ऐप में बनाएँ या अपलोड करें",
    rv_kind_doc: "दस्तावेज़ अपलोड",
    rv_optional: "वैकल्पिक",
    rv_not_filled: "अभी तक नहीं भरा गया।",
    rv_edit: "संपादित करें",
    rv_fill_in: "भरें",
    rv_cancel: "रद्द करें",
    rv_no_file: "अभी तक कोई फ़ाइल अपलोड नहीं।",
    rv_upload_more: "एक और अपलोड करें (इमेज, PDF, Word या Excel)",
    rv_upload_first: "फ़ाइल अपलोड करें (इमेज, PDF, Word या Excel)",
    rv_needs_improvement: "सुधार आवश्यक",
    rv_awaiting_rereview: "सुधारा गया — पुनः समीक्षा प्रतीक्षित",
    rv_marked_ok: "ठीक चिह्नित",
    rv_approved: "स्वीकृत",
    rv_not_approved: "अस्वीकृत",
    rv_view: "देखें",
    rv_new_inductees: "नए सदस्य",
    rv_discussion_points: "चर्चा बिंदु",
    rv_discussion_sub: "इस क्लब के लिए उठाए गए बिंदु, योगदानकर्ता सहित",
    rv_add_point: "एक चर्चा बिंदु जोड़ें",
    rv_add: "जोड़ें",
    rv_no_points: "अभी तक कोई चर्चा बिंदु नहीं।",
    rv_open: "खुला",
    rv_resolved: "हल किया गया",
    rv_reopen: "फिर से खोलें",
    rv_resolve: "हल करें",
    rv_note: "नोट",
    rv_final_action_points: "अंतिम कार्य बिंदु",
    rv_action_sub: "यात्रा से सहमत कार्य, प्रत्येक किसी व्यक्ति को सौंपा गया",
    rv_shared_with_club: "क्लब के साथ साझा",
    rv_gov_closed: "GOV बंद",
    rv_action_point_ph: "कार्य बिंदु",
    rv_assigned_to_ph: "सौंपा गया",
    rv_instructions_ph: "निर्देश (वैकल्पिक)",
    rv_assigned_to: "सौंपा गया",
    rv_unassigned: "असौंपा",
    rv_no_action_points: "कोई कार्य बिंदु दर्ज नहीं।",
    rv_add_action_point: "कार्य बिंदु जोड़ें",
    rv_save_action_points: "कार्य बिंदु सहेजें",
    rv_close_gov: "GOV बंद करें",
    rv_close_warning: "GOV बंद करने के बाद पूरी रिपोर्ट लॉक हो जाती है और कोई और संपादन नहीं किया जा सकता। आप बाद में साझा किए गए कार्य बिंदुओं को वापस बुलाकर उन्हें संपादन के लिए फिर से खोल सकते हैं।",
    rv_share_prompt: "क्या ये कार्य बिंदु क्लब के साथ साझा करें?",
    rv_share_prompt_sub: "कुछ नोट केवल डिस्ट्रिक्ट के लिए हो सकते हैं। डिफ़ॉल्ट रूप से इन्हें निजी रखा जाता है।",
    rv_close_private: "बंद करें · निजी रखें",
    rv_close_share: "बंद करें और क्लब के साथ साझा करें",
    rv_shared_status: "क्लब के साथ साझा",
    rv_private_status: "डिस्ट्रिक्ट के लिए निजी — वापस बुलाने पर संपादन फिर खुलता है",
    rv_recall: "क्लब से वापस बुलाएँ",
    rv_share: "क्लब के साथ साझा करें",
    rv_directory_pdf: "क्लब निर्देशिका (PDF)",
    rv_no_requirements: "इस वर्ष के लिए कोई आवश्यकताएँ परिभाषित नहीं की गईं।",
    rv_gov_visit_inv: "GOV यात्रा और निमंत्रण",
    rv_no_access: "आपके पास इस क्लब तक पहुँच नहीं है, या इसने तैयारी शुरू नहीं की है।",
    rv_club_report: "क्लब GOV रिपोर्ट",
    rv_closed_ro: "यह GOV बंद है — केवल पढ़ने योग्य",
    rv_submitted_incomplete: "सबमिट किया गया (कुछ आइटम शेष)",
    rv_submitted_by_club: "क्लब द्वारा सबमिट किया गया",
    rv_prep_in_progress: "तैयारी प्रगति पर",
    rv_breadcrumb_district: "डिस्ट्रिक्ट",
    rv_not_set: "निर्धारित नहीं",
    rv_confirmed: "पुष्ट",
    rv_members: "सदस्य",
    rv_registered_cap: "पंजीकृत",
    rv_board_gender: "बोर्ड लिंग",
    rv_prompt_improve: "क्या सुधार की आवश्यकता है? (क्लब के लिए टिप्पणी)",
    rv_prompt_note: "इस बिंदु के लिए एक नोट जोड़ें",
    rv_or_upload: "…या इसके बजाय एक दस्तावेज़ अपलोड करें",
  },
  kn: {
    brand: "ರೋಟರಿ GOV ವರದಿ ಬಿಲ್ಡರ್",
    login_hero_kicker: "ಜಿಲ್ಲೆ 3192 · ರೋಟರಿ ವರ್ಷ 2026-27",
    login_hero_title: "ಗವರ್ನರ್ ಅವರ ಅಧಿಕೃತ ಭೇಟಿಗಾಗಿ ಅಧಿಕೃತ ಕಾರ್ಯಾಚರಣೆ ವೇದಿಕೆ.",
    login_hero_sub: "ಸುರಕ್ಷಿತ, ಪ್ಲಾಟ್‌ಫಾರ್ಮ್-ಸ್ವಾಮ್ಯದ ಸೈನ್-ಇನ್. ನಿಮ್ಮ ರುಜುವಾತುಗಳು ಮತ್ತು ಸೆಷನ್‌ಗಳು ಎಂದಿಗೂ ಪ್ಲಾಟ್‌ಫಾರ್ಮ್‌ನ ಸರ್ವರ್‌ಗಳಿಂದ ಹೊರಹೋಗುವುದಿಲ್ಲ.",
    login_hero_secure: "HttpOnly ಸೆಷನ್ · Argon2id ರುಜುವಾತು ಸಂಗ್ರಹ",
    signin_kicker: "ಸೈನ್ ಇನ್",
    signin_title: "ರೋಟರಿ GOV ವರದಿ ಬಿಲ್ಡರ್‌ಗೆ ಸೈನ್ ಇನ್ ಮಾಡಿ",
    email: "ಇಮೇಲ್",
    password: "ಪಾಸ್‌ವರ್ಡ್",
    signin_btn: "ಸೈನ್ ಇನ್",
    forgot: "ನಿಮ್ಮ ಪಾಸ್‌ವರ್ಡ್ ಮರೆತಿರಾ?",
    login_footer: "ಸುರಕ್ಷಿತ ಸರ್ವರ್-ಸೈಡ್ ಸೆಷನ್ · ಬ್ರೌಸರ್‌ನಲ್ಲಿ ಟೋಕನ್ ಇಲ್ಲ",
    invalid_login: "ಅಮಾನ್ಯ ಇಮೇಲ್ ಅಥವಾ ಪಾಸ್‌ವರ್ಡ್",
    language: "ಭಾಷೆ",
    loading: "ಲೋಡ್ ಆಗುತ್ತಿದೆ…",
    save: "ಉಳಿಸಿ",
    consent: "ಕ್ಲಬ್ ಡೈರೆಕ್ಟರಿ, ಅಧಿಕೃತ ರೋಟರಿ ಸಂವಹನ ಮತ್ತು ಗವರ್ನರ್ ಅವರ ಅಧಿಕೃತ ಭೇಟಿಗಾಗಿ ನಾನು ಇಲ್ಲಿ ನೀಡುವ ವೈಯಕ್ತಿಕ ಮಾಹಿತಿಯನ್ನು ಸಂಗ್ರಹಿಸಲು ಮತ್ತು ಬಳಸಲು ರೋಟರಿ ಮತ್ತು ನನ್ನ ಕ್ಲಬ್‌ಗೆ ನಾನು ಅಧಿಕಾರ ನೀಡುತ್ತೇನೆ. ಮಾಹಿತಿ ನಿಖರವಾಗಿದೆ ಎಂದು ನಾನು ಖಚಿತಪಡಿಸುತ್ತೇನೆ.",
    prof_consent_title: "ಅಧಿಕಾರ ಮತ್ತು ಸಮ್ಮತಿ",
    prof_directory_details: "ಡೈರೆಕ್ಟರಿ ವಿವರಗಳು",
    prof_no_member_title: "ಲಿಂಕ್ ಮಾಡಿದ ಸದಸ್ಯ ಇಲ್ಲ",
    prof_no_member_sub: "ನಿಮ್ಮ ಖಾತೆ ಯಾವುದೇ ಸದಸ್ಯ ದಾಖಲೆಗೆ ಲಿಂಕ್ ಆಗಿಲ್ಲ, ಆದ್ದರಿಂದ ಸಂಪಾದಿಸಲು ಪ್ರೊಫೈಲ್ ಇಲ್ಲ.",
    prof_complete: "ಪ್ರೊಫೈಲ್ ಪೂರ್ಣ",
    prof_incomplete: "ಪ್ರೊಫೈಲ್ ಅಪೂರ್ಣ",
    prof_call_name: "ಕರೆಯುವ ಹೆಸರು",
    prof_mobile: "ಮೊಬೈಲ್",
    prof_classification: "ವರ್ಗೀಕರಣ",
    prof_spouse_name: "ಸಂಗಾತಿಯ ಹೆಸರು",
    prof_address: "ವಿಳಾಸ",
    prof_occasions: "ಸಂದರ್ಭಗಳು · ದಿನ ಮತ್ತು ತಿಂಗಳು ಮಾತ್ರ",
    prof_birthday: "ನನ್ನ ಜನ್ಮದಿನ",
    prof_spouse_birthday: "ಸಂಗಾತಿಯ ಜನ್ಮದಿನ",
    prof_anniversary: "ವಾರ್ಷಿಕೋತ್ಸವ",
    prof_privacy: "ಗೌಪ್ಯತೆ: ನಾವು ವರ್ಷ, ಪೂರ್ಣ ಜನ್ಮ ದಿನಾಂಕ ಅಥವಾ ವಯಸ್ಸನ್ನು ಎಂದಿಗೂ ಸಂಗ್ರಹಿಸುವುದಿಲ್ಲ.",
    prof_save: "ಪ್ರೊಫೈಲ್ ಉಳಿಸಿ",
    prof_saved: "ಪ್ರೊಫೈಲ್ ಉಳಿಸಲಾಗಿದೆ",
    club_loading: "ನಿಮ್ಮ ಕ್ಲಬ್ ಹೋಮ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ…",
    club_load_error: "ನಿಮ್ಮ ಕ್ಲಬ್ ಹೋಮ್ ಲೋಡ್ ಮಾಡಲಾಗಲಿಲ್ಲ.",
    club_govs_visit: "ಗವರ್ನರ್ ಭೇಟಿ",
    club_not_scheduled: "ಇನ್ನೂ ನಿಗದಿಯಾಗಿಲ್ಲ",
    club_mock_gov: "ಮಾಕ್ GOV",
    club_schedule_now: "ಈಗ ನಿಗದಿಪಡಿಸಿ",
    club_confirm: "ದೃಢೀಕರಿಸಿ",
    club_proposed: "ಪ್ರಸ್ತಾಪಿಸಲಾಗಿದೆ",
    club_schedule_mock_title: "ಮಾಕ್ GOV ನಿಗದಿಪಡಿಸಿ",
    club_schedule_mock_desc: "ಸಿದ್ಧತೆ ಪರಿಶೀಲಿಸಲು AG GOV ಗಿಂತ ಸುಮಾರು 5–10 ದಿನ ಮೊದಲು ಭೇಟಿ ನೀಡುತ್ತಾರೆ. ಗವರ್ನರ್ ಭೇಟಿಗಿಂತ ಮೊದಲಿನ ದಿನಾಂಕ ಆಯ್ಕೆಮಾಡಿ; AG ಅದನ್ನು ದೃಢೀಕರಿಸುತ್ತಾರೆ.",
    club_propose_date: "ದಿನಾಂಕ ಪ್ರಸ್ತಾಪಿಸಿ",
    club_pick_date: "ದಿನಾಂಕ ಆಯ್ಕೆಮಾಡಿ",
    club_add_calendar: "ಕ್ಯಾಲೆಂಡರ್‌ಗೆ ಸೇರಿಸಿ",
    club_sync: "ಸಿಂಕ್ (ಸ್ವಯಂ-ನವೀಕರಣ)",
    club_action_points_title: "ಜಿಲ್ಲೆ ಶಿಫಾರಸು ಮಾಡಿದ ಕ್ರಿಯಾ ಅಂಶಗಳು",
    club_board_title: "ನಿರ್ದೇಶಕ ಮಂಡಳಿ",
    club_board_ready: "ಮಂಡಳಿ ಸಿದ್ಧವಾಗಿದೆ",
    club_board_assign: "ಮಂಡಳಿ ಸ್ಥಾನಗಳನ್ನು ನಿಯೋಜಿಸಿ",
    club_committees_title: "ಸಮಿತಿ ನೇಮಕಾತಿಗಳು",
    club_committees_done: "ಸಮಿತಿಗಳು ನೇಮಕಗೊಂಡಿವೆ",
    club_committees_assign: "ಅಧ್ಯಕ್ಷರು ಮತ್ತು ಸದಸ್ಯರನ್ನು ನೇಮಿಸಿ",
    club_directory_title: "ಕ್ಲಬ್ ಡೈರೆಕ್ಟರಿ ರಚಿಸಿ",
    club_directory_ready: "ರೋಸ್ಟರ್ ಮತ್ತು ಡೈರೆಕ್ಟರಿ ರಚಿಸಿ",
    club_directory_locked: "ಮೊದಲು ಮಂಡಳಿ ಮತ್ತು ಸಮಿತಿಗಳನ್ನು ಪೂರ್ಣಗೊಳಿಸಿ",
    club_manage_members: "ಕ್ಲಬ್ ಸದಸ್ಯರನ್ನು ನಿರ್ವಹಿಸಿ",
    club_registered: "My Rotary ನಲ್ಲಿ ಸದಸ್ಯರು",
    club_gender_mix: "ಲಿಂಗ ಅನುಪಾತ",
    club_gender_mix_sub: "ನಿಮ್ಮ ಕ್ಲಬ್‌ನ ವೈವಿಧ್ಯತೆಯನ್ನು ಜಿಲ್ಲೆ ನೋಡಲು ಸಹಾಯ ಮಾಡಿ.",
    club_total_membership: "ಒಟ್ಟು ಸದಸ್ಯತ್ವ",
    club_board: "ಮಂಡಳಿ",
    club_new_inducted: "ಈ ವರ್ಷ ಸೇರಿದ ಹೊಸ ಸದಸ್ಯರು",
    club_male: "ಪುರುಷ",
    club_female: "ಮಹಿಳೆ",
    club_other: "ಇತರೆ",
    club_documents: "ದಾಖಲೆಗಳು",
    club_docs_admin: "ಆಡಳಿತ",
    club_docs_financial: "ಹಣಕಾಸು",
    club_no_doc_list: "ಜಿಲ್ಲೆ ಈ ವರ್ಷದ ದಾಖಲೆ ಪಟ್ಟಿಯನ್ನು ಇನ್ನೂ ಪ್ರಕಟಿಸಿಲ್ಲ.",
    club_submit: "ಜಿಲ್ಲೆಗೆ ಸಲ್ಲಿಸಿ",
    club_submitted: "ಜಿಲ್ಲೆಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ",
    club_submitted_incomplete: "ಜಿಲ್ಲೆಗೆ ಸಲ್ಲಿಸಲಾಗಿದೆ (ಕೆಲವು ಅಂಶಗಳು ಬಾಕಿ)",
    club_foundation_giving: "ಫೌಂಡೇಶನ್ ದೇಣಿಗೆ",
    club_foundation_sub: "ದ ರೋಟರಿ ಫೌಂಡೇಶನ್ ಕೊಡುಗೆಗಳು (USD).",
    club_total_prev: "ಹಿಂದಿನ ವರ್ಷದವರೆಗೆ ಒಟ್ಟು",
    club_this_year: "ಈ ವರ್ಷ",
    club_annual_fund: "ವಾರ್ಷಿಕ ನಿಧಿ",
    club_polio_plus: "ಪೋಲಿಯೋಪ್ಲಸ್ ನಿಧಿ",
    club_other_funds: "ಇತರ ನಿಧಿಗಳು",
    club_endowment: "ದತ್ತಿ ನಿಧಿ",
    club_total_this_year: "ಈ ವರ್ಷದ ಒಟ್ಟು",
    club_per_capita: "ಈ ವರ್ಷ ತಲಾ",
    club_recognitions: "ಮನ್ನಣೆಗಳು",
    club_global_grants: "ಗ್ಲೋಬಲ್ ಗ್ರಾಂಟ್ಸ್",
    club_grants_sub: "ಯೋಜನೆಗಳು ಮತ್ತು ಹಣಕಾಸು ಪಾಲುಗಳು (USD)",
    club_ongoing: "ನಡೆಯುತ್ತಿರುವ",
    club_initiated_this_year: "ಈ ವರ್ಷ ಪ್ರಾರಂಭಿಸಲಾಗಿದೆ",
    club_total_grant_funding: "ಒಟ್ಟು ಅನುದಾನ ಹಣಕಾಸು",
    club_all_board_filled: "ಎಲ್ಲಾ ಮಂಡಳಿ ಸ್ಥಾನಗಳು ಭರ್ತಿಯಾಗಿವೆ",
    club_no_visit: "ಇನ್ನೂ ಯಾವುದೇ ಭೇಟಿ ಯೋಜಿಸಲಾಗಿಲ್ಲ.",
    dist_loading: "ನಿಮ್ಮ ಜಿಲ್ಲೆ ಲೋಡ್ ಆಗುತ್ತಿದೆ…",
    dist_loading_hint: "ಪ್ರತಿ ಕ್ಲಬ್‌ನ ಸಿದ್ಧತೆ ಸಂಗ್ರಹಿಸಲಾಗುತ್ತಿದೆ — ಇದು ಕೆಲವು ಸೆಕೆಂಡುಗಳನ್ನು ತೆಗೆದುಕೊಳ್ಳಬಹುದು.",
    dist_load_failed: "ಜಿಲ್ಲಾ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್ ಲೋಡ್ ಮಾಡಲಾಗಲಿಲ್ಲ.",
    dist_load_failed_hint: "ಸಂಪರ್ಕದ ಸಮಯ ಮೀರಿರಬಹುದು. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.",
    retry: "ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ",
    dist_good_day: "ನಮಸ್ಕಾರ",
    dist_overview_sub: "ಈ ವರ್ಷದ ನಿಮ್ಮ ಗವರ್ನರ್ ಅಧಿಕೃತ ಭೇಟಿಯ ಅವಲೋಕನ.",
    dist_clubs: "ಕ್ಲಬ್‌ಗಳು",
    dist_completed: "ಪೂರ್ಣಗೊಂಡಿದೆ",
    dist_in_progress: "ಪ್ರಗತಿಯಲ್ಲಿದೆ",
    dist_not_started: "ಪ್ರಾರಂಭವಾಗಿಲ್ಲ",
    dist_find_club: "ಕ್ಲಬ್ ಹುಡುಕಿ…",
    dist_all_clubs: "ಎಲ್ಲಾ ಕ್ಲಬ್‌ಗಳು",
    dist_all_zones: "ಎಲ್ಲಾ ವಲಯಗಳು",
    dist_clubs_count: "ಕ್ಲಬ್‌ಗಳು",
    dist_zone_governor: "ವಲಯ ಗವರ್ನರ್",
    dist_ag: "AG",
    dist_card_view: "ಕಾರ್ಡ್ ವೀಕ್ಷಣೆ",
    dist_table_view: "ಟೇಬಲ್ ವೀಕ್ಷಣೆ",
    dist_no_zones: "ಈ ರೋಟರಿ ವರ್ಷಕ್ಕೆ ಇನ್ನೂ ಯಾವುದೇ ವಲಯ ಅಥವಾ ಕ್ಲಬ್ ಹೊಂದಿಸಲಾಗಿಲ್ಲ.",
    dist_no_clubs: "ಯಾವುದೇ ಕ್ಲಬ್ ಕಂಡುಬಂದಿಲ್ಲ.",
    dist_upcoming_visits: "ಮುಂಬರುವ ಭೇಟಿಗಳು",
    dist_no_visits: "ಇನ್ನೂ ಯಾವುದೇ ಭೇಟಿ ನಿಗದಿಯಾಗಿಲ್ಲ.",
    dist_date_tbc: "ದಿನಾಂಕ ನಿರ್ಧರಿಸಬೇಕಿದೆ",
    dist_my_rotary_reg: "My Rotary ನೋಂದಣಿ",
    dist_across_district: "ಇಡೀ ಜಿಲ್ಲೆಯಾದ್ಯಂತ",
    dist_registered: "ನೋಂದಾಯಿತ",
    dist_not_registered: "ನೋಂದಾಯಿಸಿಲ್ಲ",
    dist_clubs_done: "ಕ್ಲಬ್‌ಗಳು ಪೂರ್ಣ",
    dist_col_club: "ಕ್ಲಬ್",
    dist_col_zone: "ವಲಯ",
    dist_col_gov_date: "GOV ದಿನಾಂಕ",
    dist_col_mock: "ಮಾಕ್ GOV",
    dist_col_docs: "ದಾಖಲೆ %",
    dist_col_submission: "ಸಲ್ಲಿಕೆ",
    dist_mock_done: "ಪೂರ್ಣ",
    dist_mock_proposed: "ಪ್ರಸ್ತಾಪಿತ",
    dist_mock_gov_done: "ಮಾಕ್ GOV ಪೂರ್ಣ",
    dist_mock_gov_proposed: "ಮಾಕ್ GOV ಪ್ರಸ್ತಾಪಿತ",
    dist_submitted: "ಸಲ್ಲಿಸಲಾಗಿದೆ",
    dist_submitted_incomplete: "ಸಲ್ಲಿಸಲಾಗಿದೆ · ಅಪೂರ್ಣ",
    dist_not_submitted: "ಸಲ್ಲಿಸಿಲ್ಲ",
    gp_section_title: "ಫೌಂಡೇಶನ್, ಗ್ರಾಂಟ್ಸ್ ಮತ್ತು ಸದಸ್ಯತ್ವ",
    gp_export: "ಎಕ್ಸೆಲ್ ರಫ್ತು",
    gp_membership: "ಸದಸ್ಯತ್ವ",
    gp_whole_district: "ಇಡೀ ಜಿಲ್ಲೆ",
    gp_no_inductions: "ಇನ್ನೂ ಯಾವುದೇ ಸೇರ್ಪಡೆ ದಾಖಲಾಗಿಲ್ಲ.",
    gp_total_label: "ಒಟ್ಟು",
    gp_per_capita: "ತಲಾ",
    gp_fund_split: "ಈ ವರ್ಷದ ನಿಧಿ ವಿಭಜನೆ",
    gp_select_zone_hint: "ಪ್ರತಿ ಕ್ಲಬ್‌ನ ನಿಧಿವಾರು ದೇಣಿಗೆ ನೋಡಲು ಮೇಲೆ ಒಂದು ವಲಯ ಆಯ್ಕೆಮಾಡಿ.",
    gp_projects_underway: "ನಡೆಯುತ್ತಿರುವ ಯೋಜನೆಗಳು",
    gp_underway_sub: "ನಡೆಯುತ್ತಿರುವ + ಈ ವರ್ಷ ಪ್ರಾರಂಭಿಸಿದ",
    gp_total_outlay: "ಒಟ್ಟು ವೆಚ್ಚ",
    gp_outlay_sub: "ನಡೆಯುತ್ತಿರುವ ಯೋಜನೆಗಳ ಹಣಕಾಸು",
    gp_club_giving_by_fund: "ನಿಧಿವಾರು ಕ್ಲಬ್ ದೇಣಿಗೆ",
    gp_polio: "ಪೋಲಿಯೋಪ್ಲಸ್",
    gp_endowment_short: "ದತ್ತಿ",
    gp_scope_year_suffix: "ಈ ವರ್ಷ",
    gp_this_yr: "ಈ ವರ್ಷ",
    gp_till_last_yr: "ಹಿಂದಿನ ವರ್ಷದವರೆಗೆ",
    gp_rec_sub: "ಫೌಂಡೇಶನ್ ದೇಣಿಗೆಯ ಭಾಗ · ಎಣಿಕೆ",
    gp_no_phf: "ಇನ್ನೂ PHF / ಮೇಜರ್ ಡೋನರ್ ಡೇಟಾ ಇಲ್ಲ.",
    gp_by_club: "ಕ್ಲಬ್‌ವಾರು",
    gp_till_last_year: "ಹಿಂದಿನ ವರ್ಷದವರೆಗೆ",
    gp_no_clubs_zone: "ಈ ವಲಯದಲ್ಲಿ ಇನ್ನೂ ಕ್ಲಬ್‌ಗಳಿಲ್ಲ.",
    gp_no_data: "ಇನ್ನೂ ಯಾವುದೇ ಡೇಟಾ ನಮೂದಿಸಿಲ್ಲ.",
    rv_loading_roster: "ರೋಸ್ಟರ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ…",
    rv_not_built: "ಇನ್ನೂ ರಚಿಸಿಲ್ಲ.",
    rv_vacant: "ಖಾಲಿ",
    rv_position_ph: "ಸ್ಥಾನ",
    rv_vacant_opt: "— ಖಾಲಿ —",
    rv_add_position: "ಸ್ಥಾನ ಸೇರಿಸಿ",
    rv_save_roster: "ರೋಸ್ಟರ್ ಉಳಿಸಿ",
    rv_kind_info: "ಆ್ಯಪ್‌ನಲ್ಲಿ ಭರ್ತಿಮಾಡಿ",
    rv_kind_template: "ಟೆಂಪ್ಲೇಟ್ ಎಕ್ಸೆಲ್",
    rv_kind_both: "ಆ್ಯಪ್‌ನಲ್ಲಿ ರಚಿಸಿ ಅಥವಾ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ",
    rv_kind_doc: "ದಾಖಲೆ ಅಪ್‌ಲೋಡ್",
    rv_optional: "ಐಚ್ಛಿಕ",
    rv_not_filled: "ಇನ್ನೂ ಭರ್ತಿಮಾಡಿಲ್ಲ.",
    rv_edit: "ಸಂಪಾದಿಸಿ",
    rv_fill_in: "ಭರ್ತಿಮಾಡಿ",
    rv_cancel: "ರದ್ದುಮಾಡಿ",
    rv_no_file: "ಇನ್ನೂ ಯಾವುದೇ ಫೈಲ್ ಅಪ್‌ಲೋಡ್ ಆಗಿಲ್ಲ.",
    rv_upload_more: "ಇನ್ನೊಂದು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ (ಚಿತ್ರ, PDF, Word ಅಥವಾ Excel)",
    rv_upload_first: "ಫೈಲ್ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ (ಚಿತ್ರ, PDF, Word ಅಥವಾ Excel)",
    rv_needs_improvement: "ಸುಧಾರಣೆ ಅಗತ್ಯ",
    rv_awaiting_rereview: "ಸರಿಪಡಿಸಲಾಗಿದೆ — ಮರುಪರಿಶೀಲನೆ ಬಾಕಿ",
    rv_marked_ok: "ಸರಿ ಎಂದು ಗುರುತಿಸಲಾಗಿದೆ",
    rv_approved: "ಅನುಮೋದಿಸಲಾಗಿದೆ",
    rv_not_approved: "ಅನುಮೋದಿಸಲಾಗಿಲ್ಲ",
    rv_view: "ವೀಕ್ಷಿಸಿ",
    rv_new_inductees: "ಹೊಸ ಸದಸ್ಯರು",
    rv_discussion_points: "ಚರ್ಚಾ ಅಂಶಗಳು",
    rv_discussion_sub: "ಈ ಕ್ಲಬ್‌ಗಾಗಿ ಎತ್ತಿದ ಅಂಶಗಳು, ಕೊಡುಗೆ ನೀಡಿದವರ ಸಹಿತ",
    rv_add_point: "ಚರ್ಚಾ ಅಂಶ ಸೇರಿಸಿ",
    rv_add: "ಸೇರಿಸಿ",
    rv_no_points: "ಇನ್ನೂ ಯಾವುದೇ ಚರ್ಚಾ ಅಂಶಗಳಿಲ್ಲ.",
    rv_open: "ತೆರೆದಿದೆ",
    rv_resolved: "ಪರಿಹರಿಸಲಾಗಿದೆ",
    rv_reopen: "ಮತ್ತೆ ತೆರೆಯಿರಿ",
    rv_resolve: "ಪರಿಹರಿಸಿ",
    rv_note: "ಟಿಪ್ಪಣಿ",
    rv_final_action_points: "ಅಂತಿಮ ಕ್ರಿಯಾ ಅಂಶಗಳು",
    rv_action_sub: "ಭೇಟಿಯಿಂದ ಒಪ್ಪಿದ ಕ್ರಮಗಳು, ಪ್ರತಿಯೊಂದೂ ಒಬ್ಬ ವ್ಯಕ್ತಿಗೆ ನಿಯೋಜಿತ",
    rv_shared_with_club: "ಕ್ಲಬ್‌ನೊಂದಿಗೆ ಹಂಚಲಾಗಿದೆ",
    rv_gov_closed: "GOV ಮುಚ್ಚಲಾಗಿದೆ",
    rv_action_point_ph: "ಕ್ರಿಯಾ ಅಂಶ",
    rv_assigned_to_ph: "ನಿಯೋಜಿಸಲಾಗಿದೆ",
    rv_instructions_ph: "ಸೂಚನೆಗಳು (ಐಚ್ಛಿಕ)",
    rv_assigned_to: "ನಿಯೋಜಿಸಲಾಗಿದೆ",
    rv_unassigned: "ನಿಯೋಜಿಸಿಲ್ಲ",
    rv_no_action_points: "ಯಾವುದೇ ಕ್ರಿಯಾ ಅಂಶಗಳು ದಾಖಲಾಗಿಲ್ಲ.",
    rv_add_action_point: "ಕ್ರಿಯಾ ಅಂಶ ಸೇರಿಸಿ",
    rv_save_action_points: "ಕ್ರಿಯಾ ಅಂಶಗಳನ್ನು ಉಳಿಸಿ",
    rv_close_gov: "GOV ಮುಚ್ಚಿ",
    rv_close_warning: "ನೀವು GOV ಮುಚ್ಚಿದ ನಂತರ ಇಡೀ ವರದಿ ಲಾಕ್ ಆಗುತ್ತದೆ ಮತ್ತು ಹೆಚ್ಚಿನ ಸಂಪಾದನೆ ಸಾಧ್ಯವಿಲ್ಲ. ಹಂಚಿದ ಕ್ರಿಯಾ ಅಂಶಗಳನ್ನು ನಂತರ ಹಿಂಪಡೆದು ಸಂಪಾದನೆಗೆ ಮತ್ತೆ ತೆರೆಯಬಹುದು.",
    rv_share_prompt: "ಈ ಕ್ರಿಯಾ ಅಂಶಗಳನ್ನು ಕ್ಲಬ್‌ನೊಂದಿಗೆ ಹಂಚಬೇಕೆ?",
    rv_share_prompt_sub: "ಕೆಲವು ಟಿಪ್ಪಣಿಗಳು ಜಿಲ್ಲೆಗೆ ಮಾತ್ರ ಆಗಿರಬಹುದು. ಡೀಫಾಲ್ಟ್ ಆಗಿ ಖಾಸಗಿಯಾಗಿ ಇಡಲಾಗುತ್ತದೆ.",
    rv_close_private: "ಮುಚ್ಚಿ · ಖಾಸಗಿಯಾಗಿ ಇಡಿ",
    rv_close_share: "ಮುಚ್ಚಿ ಮತ್ತು ಕ್ಲಬ್‌ನೊಂದಿಗೆ ಹಂಚಿ",
    rv_shared_status: "ಕ್ಲಬ್‌ನೊಂದಿಗೆ ಹಂಚಲಾಗಿದೆ",
    rv_private_status: "ಜಿಲ್ಲೆಗೆ ಖಾಸಗಿ — ಹಿಂಪಡೆದರೆ ಸಂಪಾದನೆ ಮತ್ತೆ ತೆರೆಯುತ್ತದೆ",
    rv_recall: "ಕ್ಲಬ್‌ನಿಂದ ಹಿಂಪಡೆ",
    rv_share: "ಕ್ಲಬ್‌ನೊಂದಿಗೆ ಹಂಚಿ",
    rv_directory_pdf: "ಕ್ಲಬ್ ಡೈರೆಕ್ಟರಿ (PDF)",
    rv_no_requirements: "ಈ ವರ್ಷಕ್ಕೆ ಯಾವುದೇ ಅಗತ್ಯತೆಗಳನ್ನು ವ್ಯಾಖ್ಯಾನಿಸಿಲ್ಲ.",
    rv_gov_visit_inv: "GOV ಭೇಟಿ ಮತ್ತು ಆಮಂತ್ರಣಗಳು",
    rv_no_access: "ಈ ಕ್ಲಬ್‌ಗೆ ನಿಮಗೆ ಪ್ರವೇಶವಿಲ್ಲ, ಅಥವಾ ಅದು ತಯಾರಿ ಆರಂಭಿಸಿಲ್ಲ.",
    rv_club_report: "ಕ್ಲಬ್ GOV ವರದಿ",
    rv_closed_ro: "ಈ GOV ಮುಚ್ಚಲಾಗಿದೆ — ಓದಲು ಮಾತ್ರ",
    rv_submitted_incomplete: "ಸಲ್ಲಿಸಲಾಗಿದೆ (ಕೆಲವು ಅಂಶಗಳು ಬಾಕಿ)",
    rv_submitted_by_club: "ಕ್ಲಬ್ ಸಲ್ಲಿಸಿದೆ",
    rv_prep_in_progress: "ತಯಾರಿ ಪ್ರಗತಿಯಲ್ಲಿದೆ",
    rv_breadcrumb_district: "ಜಿಲ್ಲೆ",
    rv_not_set: "ನಿಗದಿಪಡಿಸಿಲ್ಲ",
    rv_confirmed: "ದೃಢೀಕರಿಸಲಾಗಿದೆ",
    rv_members: "ಸದಸ್ಯರು",
    rv_registered_cap: "ನೋಂದಾಯಿತ",
    rv_board_gender: "ಮಂಡಳಿ ಲಿಂಗ",
    rv_prompt_improve: "ಏನನ್ನು ಸುಧಾರಿಸಬೇಕು? (ಕ್ಲಬ್‌ಗಾಗಿ ಟಿಪ್ಪಣಿ)",
    rv_prompt_note: "ಈ ಅಂಶಕ್ಕೆ ಟಿಪ್ಪಣಿ ಸೇರಿಸಿ",
    rv_or_upload: "…ಅಥವಾ ಬದಲಿಗೆ ದಾಖಲೆ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ",
  },
  te: {
    brand: "రోటరీ GOV రిపోర్ట్ బిల్డర్",
    login_hero_kicker: "డిస్ట్రిక్ట్ 3192 · రోటరీ సంవత్సరం 2026-27",
    login_hero_title: "గవర్నర్ అధికారిక సందర్శన కోసం అధికారిక కార్యకలాపాల వేదిక.",
    login_hero_sub: "సురక్షితమైన, ప్లాట్‌ఫారమ్-యాజమాన్య సైన్-ఇన్. మీ ఆధారాలు మరియు సెషన్‌లు ఎప్పుడూ ప్లాట్‌ఫారమ్ సర్వర్‌లను వదిలిపెట్టవు.",
    login_hero_secure: "HttpOnly సెషన్ · Argon2id ఆధారాల నిల్వ",
    signin_kicker: "సైన్ ఇన్",
    signin_title: "రోటరీ GOV రిపోర్ట్ బిల్డర్‌కు సైన్ ఇన్ చేయండి",
    email: "ఇమెయిల్",
    password: "పాస్‌వర్డ్",
    signin_btn: "సైన్ ఇన్",
    forgot: "మీ పాస్‌వర్డ్ మర్చిపోయారా?",
    login_footer: "సురక్షిత సర్వర్-సైడ్ సెషన్ · బ్రౌజర్‌లో టోకెన్‌లు లేవు",
    invalid_login: "చెల్లని ఇమెయిల్ లేదా పాస్‌వర్డ్",
    language: "భాష",
    loading: "లోడ్ అవుతోంది…",
    save: "సేవ్ చేయండి",
    consent: "క్లబ్ డైరెక్టరీ, అధికారిక రోటరీ కమ్యూనికేషన్ మరియు గవర్నర్ అధికారిక సందర్శన కోసం నేను ఇక్కడ అందించే వ్యక్తిగత వివరాలను నిల్వ చేసి ఉపయోగించడానికి రోటరీ మరియు నా క్లబ్‌కు నేను అధికారం ఇస్తున్నాను. సమాచారం ఖచ్చితమైనదని నేను నిర్ధారిస్తున్నాను.",
    prof_consent_title: "అధికారం & సమ్మతి",
    prof_directory_details: "డైరెక్టరీ వివరాలు",
    prof_no_member_title: "లింక్ చేసిన సభ్యుడు లేరు",
    prof_no_member_sub: "మీ ఖాతా ఏ సభ్యుడి రికార్డ్‌కు లింక్ కాలేదు, కాబట్టి సవరించడానికి ప్రొఫైల్ లేదు.",
    prof_complete: "ప్రొఫైల్ పూర్తయింది",
    prof_incomplete: "ప్రొఫైల్ అసంపూర్ణం",
    prof_call_name: "పిలిచే పేరు",
    prof_mobile: "మొబైల్",
    prof_classification: "వర్గీకరణ",
    prof_spouse_name: "జీవిత భాగస్వామి పేరు",
    prof_address: "చిరునామా",
    prof_occasions: "సందర్భాలు · రోజు & నెల మాత్రమే",
    prof_birthday: "నా పుట్టినరోజు",
    prof_spouse_birthday: "జీవిత భాగస్వామి పుట్టినరోజు",
    prof_anniversary: "వార్షికోత్సవం",
    prof_privacy: "గోప్యత: మేము సంవత్సరం, పూర్తి పుట్టిన తేదీ లేదా వయస్సును ఎప్పుడూ నిల్వ చేయము.",
    prof_save: "ప్రొఫైల్ సేవ్ చేయండి",
    prof_saved: "ప్రొఫైల్ సేవ్ చేయబడింది",
    club_loading: "మీ క్లబ్ హోమ్ లోడ్ అవుతోంది…",
    club_load_error: "మీ క్లబ్ హోమ్ లోడ్ చేయడం సాధ్యం కాలేదు.",
    club_govs_visit: "గవర్నర్ సందర్శన",
    club_not_scheduled: "ఇంకా షెడ్యూల్ కాలేదు",
    club_mock_gov: "మాక్ GOV",
    club_schedule_now: "ఇప్పుడు షెడ్యూల్ చేయండి",
    club_confirm: "నిర్ధారించండి",
    club_proposed: "ప్రతిపాదించబడింది",
    club_schedule_mock_title: "మాక్ GOV షెడ్యూల్ చేయండి",
    club_schedule_mock_desc: "సంసిద్ధతను తనిఖీ చేయడానికి AG GOV కి సుమారు 5–10 రోజుల ముందు సందర్శిస్తారు. గవర్నర్ సందర్శనకు ముందు తేదీని ఎంచుకోండి; AG దాన్ని నిర్ధారిస్తారు.",
    club_propose_date: "తేదీని ప్రతిపాదించండి",
    club_pick_date: "తేదీని ఎంచుకోండి",
    club_add_calendar: "క్యాలెండర్‌కు జోడించండి",
    club_sync: "సింక్ (స్వయం-నవీకరణ)",
    club_action_points_title: "జిల్లా సిఫార్సు చేసిన కార్యాచరణ అంశాలు",
    club_board_title: "డైరెక్టర్ల బోర్డు",
    club_board_ready: "బోర్డు సిద్ధంగా ఉంది",
    club_board_assign: "బోర్డు స్థానాలను కేటాయించండి",
    club_committees_title: "కమిటీ నియామకాలు",
    club_committees_done: "కమిటీలు నియమించబడ్డాయి",
    club_committees_assign: "అధ్యక్షులు & సభ్యులను నియమించండి",
    club_directory_title: "క్లబ్ డైరెక్టరీని రూపొందించండి",
    club_directory_ready: "రోస్టర్ & డైరెక్టరీని రూపొందించండి",
    club_directory_locked: "ముందుగా బోర్డు & కమిటీలను పూర్తి చేయండి",
    club_manage_members: "క్లబ్ సభ్యులను నిర్వహించండి",
    club_registered: "My Rotary లో సభ్యులు",
    club_gender_mix: "లింగ నిష్పత్తి",
    club_gender_mix_sub: "మీ క్లబ్ వైవిధ్యాన్ని జిల్లా చూడటానికి సహాయపడండి.",
    club_total_membership: "మొత్తం సభ్యత్వం",
    club_board: "బోర్డు",
    club_new_inducted: "ఈ సంవత్సరం చేరిన కొత్త సభ్యులు",
    club_male: "పురుషుడు",
    club_female: "మహిళ",
    club_other: "ఇతర",
    club_documents: "పత్రాలు",
    club_docs_admin: "పరిపాలన",
    club_docs_financial: "ఆర్థిక",
    club_no_doc_list: "జిల్లా ఈ సంవత్సరం పత్రాల జాబితాను ఇంకా ప్రచురించలేదు.",
    club_submit: "జిల్లాకు సమర్పించండి",
    club_submitted: "జిల్లాకు సమర్పించబడింది",
    club_submitted_incomplete: "జిల్లాకు సమర్పించబడింది (కొన్ని అంశాలు మిగిలి ఉన్నాయి)",
    club_foundation_giving: "ఫౌండేషన్ విరాళం",
    club_foundation_sub: "ద రోటరీ ఫౌండేషన్ విరాళాలు (USD).",
    club_total_prev: "మునుపటి సంవత్సరం వరకు మొత్తం",
    club_this_year: "ఈ సంవత్సరం",
    club_annual_fund: "వార్షిక నిధి",
    club_polio_plus: "పోలియోప్లస్ నిధి",
    club_other_funds: "ఇతర నిధులు",
    club_endowment: "ఎండోమెంట్ నిధి",
    club_total_this_year: "ఈ సంవత్సరం మొత్తం",
    club_per_capita: "ఈ సంవత్సరం తలసరి",
    club_recognitions: "గుర్తింపులు",
    club_global_grants: "గ్లోబల్ గ్రాంట్స్",
    club_grants_sub: "ప్రాజెక్ట్‌లు మరియు నిధుల వాటాలు (USD)",
    club_ongoing: "కొనసాగుతున్న",
    club_initiated_this_year: "ఈ సంవత్సరం ప్రారంభించబడింది",
    club_total_grant_funding: "మొత్తం గ్రాంట్ నిధులు",
    club_all_board_filled: "అన్ని బోర్డు స్థానాలు భర్తీ అయ్యాయి",
    club_no_visit: "ఇంకా ఏ సందర్శన ప్రణాళిక లేదు.",
    dist_loading: "మీ జిల్లా లోడ్ అవుతోంది…",
    dist_loading_hint: "ప్రతి క్లబ్ సంసిద్ధతను సేకరిస్తోంది — దీనికి కొన్ని సెకన్లు పట్టవచ్చు.",
    dist_load_failed: "జిల్లా డాష్‌బోర్డ్ లోడ్ చేయడం సాధ్యం కాలేదు.",
    dist_load_failed_hint: "కనెక్షన్ సమయం ముగిసి ఉండవచ్చు. దయచేసి మళ్లీ ప్రయత్నించండి.",
    retry: "మళ్లీ ప్రయత్నించండి",
    dist_good_day: "నమస్కారం",
    dist_overview_sub: "ఈ సంవత్సరం మీ గవర్నర్ అధికారిక సందర్శన అవలోకనం.",
    dist_clubs: "క్లబ్‌లు",
    dist_completed: "పూర్తయింది",
    dist_in_progress: "పురోగతిలో ఉంది",
    dist_not_started: "ప్రారంభం కాలేదు",
    dist_find_club: "క్లబ్‌ను కనుగొనండి…",
    dist_all_clubs: "అన్ని క్లబ్‌లు",
    dist_all_zones: "అన్ని జోన్‌లు",
    dist_clubs_count: "క్లబ్‌లు",
    dist_zone_governor: "జోన్ గవర్నర్",
    dist_ag: "AG",
    dist_card_view: "కార్డ్ వీక్షణ",
    dist_table_view: "టేబుల్ వీక్షణ",
    dist_no_zones: "ఈ రోటరీ సంవత్సరం కోసం ఇంకా ఏ జోన్‌లు లేదా క్లబ్‌లు సెటప్ చేయబడలేదు.",
    dist_no_clubs: "క్లబ్‌లు కనుగొనబడలేదు.",
    dist_upcoming_visits: "రాబోయే సందర్శనలు",
    dist_no_visits: "ఇంకా ఏ సందర్శనలు షెడ్యూల్ కాలేదు.",
    dist_date_tbc: "తేదీ నిర్ణయించాలి",
    dist_my_rotary_reg: "My Rotary నమోదు",
    dist_across_district: "మొత్తం జిల్లా అంతటా",
    dist_registered: "నమోదైంది",
    dist_not_registered: "నమోదు కాలేదు",
    dist_clubs_done: "క్లబ్‌లు పూర్తయ్యాయి",
    dist_col_club: "క్లబ్",
    dist_col_zone: "జోన్",
    dist_col_gov_date: "GOV తేదీ",
    dist_col_mock: "మాక్ GOV",
    dist_col_docs: "పత్రాలు %",
    dist_col_submission: "సమర్పణ",
    dist_mock_done: "పూర్తయింది",
    dist_mock_proposed: "ప్రతిపాదితం",
    dist_mock_gov_done: "మాక్ GOV పూర్తయింది",
    dist_mock_gov_proposed: "మాక్ GOV ప్రతిపాదితం",
    dist_submitted: "సమర్పించబడింది",
    dist_submitted_incomplete: "సమర్పించబడింది · అసంపూర్ణం",
    dist_not_submitted: "సమర్పించలేదు",
    gp_section_title: "ఫౌండేషన్, గ్రాంట్స్ & సభ్యత్వం",
    gp_export: "ఎక్సెల్ ఎగుమతి",
    gp_membership: "సభ్యత్వం",
    gp_whole_district: "మొత్తం జిల్లా",
    gp_no_inductions: "ఇంకా ఏ ప్రవేశాలు నమోదు కాలేదు.",
    gp_total_label: "మొత్తం",
    gp_per_capita: "తలసరి",
    gp_fund_split: "ఈ సంవత్సరం నిధి విభజన",
    gp_select_zone_hint: "ప్రతి క్లబ్ నిధివారీ విరాళం చూడటానికి పైన ఒక జోన్ ఎంచుకోండి.",
    gp_projects_underway: "కొనసాగుతున్న ప్రాజెక్ట్‌లు",
    gp_underway_sub: "కొనసాగుతున్న + ఈ సంవత్సరం ప్రారంభించిన",
    gp_total_outlay: "మొత్తం వ్యయం",
    gp_outlay_sub: "కొనసాగుతున్న ప్రాజెక్ట్‌ల నిధులు",
    gp_club_giving_by_fund: "నిధివారీ క్లబ్ విరాళం",
    gp_polio: "పోలియోప్లస్",
    gp_endowment_short: "ఎండోమెంట్",
    gp_scope_year_suffix: "ఈ సంవత్సరం",
    gp_this_yr: "ఈ సంవత్సరం",
    gp_till_last_yr: "మునుపటి సంవత్సరం వరకు",
    gp_rec_sub: "ఫౌండేషన్ విరాళంలో భాగం · లెక్కలు",
    gp_no_phf: "ఇంకా PHF / మేజర్ డోనర్ డేటా లేదు.",
    gp_by_club: "క్లబ్‌వారీగా",
    gp_till_last_year: "మునుపటి సంవత్సరం వరకు",
    gp_no_clubs_zone: "ఈ జోన్‌లో ఇంకా క్లబ్‌లు లేవు.",
    gp_no_data: "ఇంకా ఏ డేటా నమోదు కాలేదు.",
    rv_loading_roster: "రోస్టర్ లోడ్ అవుతోంది…",
    rv_not_built: "ఇంకా రూపొందించలేదు.",
    rv_vacant: "ఖాళీ",
    rv_position_ph: "స్థానం",
    rv_vacant_opt: "— ఖాళీ —",
    rv_add_position: "స్థానం జోడించండి",
    rv_save_roster: "రోస్టర్ సేవ్ చేయండి",
    rv_kind_info: "యాప్‌లో నింపండి",
    rv_kind_template: "టెంప్లేట్ ఎక్సెల్",
    rv_kind_both: "యాప్‌లో రూపొందించండి లేదా అప్‌లోడ్ చేయండి",
    rv_kind_doc: "పత్రం అప్‌లోడ్",
    rv_optional: "ఐచ్ఛికం",
    rv_not_filled: "ఇంకా నింపలేదు.",
    rv_edit: "సవరించండి",
    rv_fill_in: "నింపండి",
    rv_cancel: "రద్దు చేయండి",
    rv_no_file: "ఇంకా ఏ ఫైల్ అప్‌లోడ్ కాలేదు.",
    rv_upload_more: "మరొకటి అప్‌లోడ్ చేయండి (చిత్రం, PDF, Word లేదా Excel)",
    rv_upload_first: "ఫైల్ అప్‌లోడ్ చేయండి (చిత్రం, PDF, Word లేదా Excel)",
    rv_needs_improvement: "మెరుగుదల అవసరం",
    rv_awaiting_rereview: "సరిచేయబడింది — మళ్లీ సమీక్ష పెండింగ్",
    rv_marked_ok: "సరి అని గుర్తించబడింది",
    rv_approved: "ఆమోదించబడింది",
    rv_not_approved: "ఆమోదించబడలేదు",
    rv_view: "వీక్షించండి",
    rv_new_inductees: "కొత్త సభ్యులు",
    rv_discussion_points: "చర్చా అంశాలు",
    rv_discussion_sub: "ఈ క్లబ్ కోసం లేవనెత్తిన అంశాలు, సహకరించినవారితో సహా",
    rv_add_point: "చర్చా అంశం జోడించండి",
    rv_add: "జోడించండి",
    rv_no_points: "ఇంకా ఏ చర్చా అంశాలు లేవు.",
    rv_open: "తెరిచి ఉంది",
    rv_resolved: "పరిష్కరించబడింది",
    rv_reopen: "మళ్లీ తెరవండి",
    rv_resolve: "పరిష్కరించండి",
    rv_note: "గమనిక",
    rv_final_action_points: "చివరి కార్యాచరణ అంశాలు",
    rv_action_sub: "సందర్శన నుండి అంగీకరించిన చర్యలు, ప్రతి ఒక్కటీ ఒక వ్యక్తికి కేటాయించబడింది",
    rv_shared_with_club: "క్లబ్‌తో పంచుకోబడింది",
    rv_gov_closed: "GOV మూసివేయబడింది",
    rv_action_point_ph: "కార్యాచరణ అంశం",
    rv_assigned_to_ph: "కేటాయించబడింది",
    rv_instructions_ph: "సూచనలు (ఐచ్ఛికం)",
    rv_assigned_to: "కేటాయించబడింది",
    rv_unassigned: "కేటాయించలేదు",
    rv_no_action_points: "ఏ కార్యాచరణ అంశాలు నమోదు కాలేదు.",
    rv_add_action_point: "కార్యాచరణ అంశం జోడించండి",
    rv_save_action_points: "కార్యాచరణ అంశాలను సేవ్ చేయండి",
    rv_close_gov: "GOV మూసివేయండి",
    rv_close_warning: "మీరు GOV మూసివేసిన తర్వాత మొత్తం నివేదిక లాక్ అవుతుంది మరియు ఎటువంటి సవరణలు చేయలేరు. పంచుకున్న కార్యాచరణ అంశాలను తర్వాత వెనక్కి పిలిచి సవరణ కోసం మళ్లీ తెరవవచ్చు.",
    rv_share_prompt: "ఈ కార్యాచరణ అంశాలను క్లబ్‌తో పంచుకోవాలా?",
    rv_share_prompt_sub: "కొన్ని గమనికలు జిల్లాకు మాత్రమే కావచ్చు. డిఫాల్ట్‌గా వాటిని ప్రైవేట్‌గా ఉంచుతారు.",
    rv_close_private: "మూసివేయి · ప్రైవేట్‌గా ఉంచు",
    rv_close_share: "మూసివేసి క్లబ్‌తో పంచుకో",
    rv_shared_status: "క్లబ్‌తో పంచుకోబడింది",
    rv_private_status: "జిల్లాకు ప్రైవేట్ — వెనక్కి పిలిస్తే సవరణ మళ్లీ తెరుచుకుంటుంది",
    rv_recall: "క్లబ్ నుండి వెనక్కి పిలువు",
    rv_share: "క్లబ్‌తో పంచుకో",
    rv_directory_pdf: "క్లబ్ డైరెక్టరీ (PDF)",
    rv_no_requirements: "ఈ సంవత్సరానికి ఎటువంటి అవసరాలు నిర్వచించబడలేదు.",
    rv_gov_visit_inv: "GOV సందర్శన & ఆహ్వానాలు",
    rv_no_access: "ఈ క్లబ్‌కు మీకు ప్రవేశం లేదు, లేదా అది తయారీ ప్రారంభించలేదు.",
    rv_club_report: "క్లబ్ GOV నివేదిక",
    rv_closed_ro: "ఈ GOV మూసివేయబడింది — చదవడానికి మాత్రమే",
    rv_submitted_incomplete: "సమర్పించబడింది (కొన్ని అంశాలు మిగిలి ఉన్నాయి)",
    rv_submitted_by_club: "క్లబ్ సమర్పించింది",
    rv_prep_in_progress: "తయారీ పురోగతిలో ఉంది",
    rv_breadcrumb_district: "జిల్లా",
    rv_not_set: "నిర్ణయించలేదు",
    rv_confirmed: "నిర్ధారించబడింది",
    rv_members: "సభ్యులు",
    rv_registered_cap: "నమోదైంది",
    rv_board_gender: "బోర్డు లింగం",
    rv_prompt_improve: "ఏమి మెరుగుపరచాలి? (క్లబ్ కోసం వ్యాఖ్య)",
    rv_prompt_note: "ఈ అంశానికి గమనిక జోడించండి",
    rv_or_upload: "…లేదా బదులుగా ఒక పత్రాన్ని అప్‌లోడ్ చేయండి",
  },
};

const I18nCtx = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("rp_lang") || "en");
  useEffect(() => {
    localStorage.setItem("rp_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const t = (key) => (dict[lang] && dict[lang][key]) || dict.en[key] || key;
  return <I18nCtx.Provider value={{ lang, setLang: setLangState, t }}>{children}</I18nCtx.Provider>;
}

export const useT = () => useContext(I18nCtx);

export function LanguageSelector({ compact }) {
  const { lang, setLang } = useT();
  return (
    <select
      data-testid="language-selector"
      value={lang}
      onChange={(e) => setLang(e.target.value)}
      aria-label="Select language"
      className={`rounded-full border border-border bg-background font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${compact ? "px-2 py-1 text-[12px]" : "px-3 py-1.5 text-[13px]"}`}
    >
      {LANGS.map((l) => (
        <option key={l.code} value={l.code}>{l.native}</option>
      ))}
    </select>
  );
}
